import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  Spinner,
  SpinnerSize,
  Toggle,
  TextField,
  DetailsList,
  IColumn,
  SelectionMode,
} from '@fluentui/react';
import {
  SettingsRegular,
  EyeRegular,
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './UIManagement.module.css';

interface NavItem {
  key: string;
  name: string;
  icon: string;
  visible: boolean;
  order: number;
  admin_only?: boolean;
  depends_on_ui?: string;
}

interface NavigationConfig {
  nav_items: NavItem[];
}

// UserRolesConfig removed - now handled by Feature Permissions system

interface UIGeneralSettings {
  hide_navigation?: boolean;
  show_share_button?: boolean;
  show_chat_history_button?: boolean;
  enable_chat_history?: boolean;
  show_upload_button?: boolean;
  show_web_search_button?: boolean;
}

const UIManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Navigation management state
  const [navConfig, setNavConfig] = useState<NavigationConfig>({ nav_items: [] });
  const [navLoading, setNavLoading] = useState(false);
  
  // UI general settings state
  const [uiSettings, setUiSettings] = useState<UIGeneralSettings>({});
  const [uiLoading, setUiLoading] = useState(false);

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load navigation config
      const navResponse = await authenticatedFetch('/api/ui-management/navigation/config');
      const navData = await navResponse.json();
      
      if (navData.success) {
        setNavConfig(navData.config);
      } else {
        throw new Error(navData.error || 'Failed to load navigation config');
      }
      
      // User roles config removed - now handled by Feature Permissions system

      // Load UI general settings from UI Management service
      const uiResponse = await authenticatedFetch('/api/ui-management/ui-settings');
      const uiData = await uiResponse.json();
      
      if (uiData.success && uiData.settings) {
        setUiSettings({
          hide_navigation: uiData.settings.hide_navigation || false,
          show_share_button: uiData.settings.show_share_button ?? true,
          show_chat_history_button: uiData.settings.show_chat_history_button ?? true,
          enable_chat_history: uiData.settings.enable_chat_history ?? true,
          show_upload_button: uiData.settings.show_upload_button ?? true,
          show_web_search_button: uiData.settings.show_web_search_button ?? true,
        });
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const saveNavigationConfig = async () => {
    setNavLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await authenticatedFetch('/api/ui-management/navigation/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ config: navConfig }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Navigation configuration saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to save navigation config');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save navigation config');
    } finally {
      setNavLoading(false);
    }
  };

  // saveUserRolesConfig removed - now handled by Feature Permissions system

  const saveUISettings = async () => {
    setUiLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Use UI Management service endpoint for saving
      const response = await authenticatedFetch('/api/ui-management/ui-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings: uiSettings }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('UI settings saved successfully.');
        
        // Trigger a frontend settings refresh by fetching updated settings
        try {
          const settingsResponse = await authenticatedFetch('/frontend_settings');
          const frontendSettings = await settingsResponse.json();
          
          // Dispatch event to update app state with new settings
          const event = new CustomEvent('frontendSettingsUpdated', {
            detail: frontendSettings
          });
          window.dispatchEvent(event);
          
          console.log('Frontend settings refreshed after UI settings save');
        } catch (settingsError) {
          console.error('Failed to refresh frontend settings:', settingsError);
          setSuccess('UI settings saved successfully. Please refresh the page to see changes.');
        }
        
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(data.message || data.error || 'Failed to save UI settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save UI settings');
    } finally {
      setUiLoading(false);
    }
  };

  const updateUISettings = (key: keyof UIGeneralSettings, value: boolean) => {
    setUiSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleNavItemVisibility = (key: string) => {
    setNavConfig(prev => ({
      ...prev,
      nav_items: prev.nav_items.map(item =>
        item.key === key ? { ...item, visible: !item.visible } : item
      )
    }));
  };

  const updateNavItemOrder = (key: string, newOrder: number) => {
    setNavConfig(prev => ({
      ...prev,
      nav_items: prev.nav_items.map(item =>
        item.key === key ? { ...item, order: newOrder } : item
      )
    }));
  };

  // changePageAccessType, addUserToPage, removeUserFromPage, openAddUserDialog removed
  // Page access control is now handled by Feature Permissions system

  // Navigation columns
  const navColumns: IColumn[] = [
    {
      key: 'visible',
      name: 'Visible',
      fieldName: 'visible',
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: NavItem) => (
        <Toggle
          checked={item.visible}
          onChange={() => toggleNavItemVisibility(item.key)}
        />
      )
    },
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 120,
      maxWidth: 200,
    },
    {
      key: 'icon',
      name: 'Icon',
      fieldName: 'icon',
      minWidth: 100,
      maxWidth: 120,
    },
    {
      key: 'order',
      name: 'Order',
      fieldName: 'order',
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: NavItem) => (
        <TextField
          type="number"
          value={item.order.toString()}
          onChange={(_, value) => updateNavItemOrder(item.key, parseInt(value || '0'))}
          styles={{ root: { width: 60 } }}
        />
      )
    },
    {
      key: 'admin_only',
      name: 'Admin Only',
      fieldName: 'admin_only',
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: NavItem) => item.admin_only ? 'Yes' : 'No'
    }
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <Stack horizontalAlign="center" verticalAlign="center" style={{ height: '400px' }}>
          <Spinner size={SpinnerSize.large} />
          <Text>Loading UI management settings...</Text>
        </Stack>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Stack tokens={{ childrenGap: 20 }}>
        {/* Header */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 8 }}>
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
              <SettingsRegular className={styles.headerIcon} />
              <Text variant="xxLarge" className={styles.headerTitle}>
                UI Management
              </Text>
            </Stack>
            <Text variant="medium" className={styles.headerSubtitle}>
              Manage navigation visibility and UI settings
            </Text>
          </Stack>
        </Stack>

        {/* Messages */}
        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
            {error}
          </MessageBar>
        )}

        {success && (
          <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccess(null)}>
            {success}
          </MessageBar>
        )}

        {/* Main Content */}
        <Pivot>
          <PivotItem headerText="Navigation Management" itemIcon="Navigation">
            <Stack tokens={{ childrenGap: 20 }}>
              <Text variant="large" className={styles.sectionTitle}>
                <EyeRegular className={styles.sectionIcon} />
                Left Navigation Items
              </Text>
              
              <DetailsList
                items={navConfig.nav_items.sort((a, b) => a.order - b.order)}
                columns={navColumns}
                selectionMode={SelectionMode.none}
                className={styles.detailsList}
              />
              
              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <PrimaryButton
                  iconProps={{ iconName: 'Save' }}
                  text="Save Navigation Config"
                  onClick={saveNavigationConfig}
                  disabled={navLoading}
                />
                {navLoading && <Spinner size={SpinnerSize.small} />}
              </Stack>
            </Stack>
          </PivotItem>

          <PivotItem headerText="UI Settings" itemIcon="Settings">
            <Stack tokens={{ childrenGap: 20 }}>
              <Text variant="large" className={styles.sectionTitle}>
                <SettingsRegular className={styles.sectionIcon} />
                General UI Settings
              </Text>
              
              <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: '600px' } }}>
                <Stack tokens={{ childrenGap: 8 }}>
                  <Toggle
                    label="Hide Navigation Menu"
                    checked={uiSettings.hide_navigation || false}
                    onChange={(_, checked) => updateUISettings('hide_navigation', checked || false)}
                    onText="Hidden"
                    offText="Visible"
                  />
                  <Text variant="small" style={{ color: '#605e5c', marginLeft: '20px' }}>
                    When enabled, the left navigation menu will be completely hidden from all users. This provides a full-width layout.
                  </Text>
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                  <Toggle
                    label="Show Share Button"
                    checked={uiSettings.show_share_button ?? true}
                    onChange={(_, checked) => updateUISettings('show_share_button', checked ?? true)}
                    onText="Shown"
                    offText="Hidden"
                  />
                  <Text variant="small" style={{ color: '#605e5c', marginLeft: '20px' }}>
                    Controls the visibility of the share button in the chat interface.
                  </Text>
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                  <Toggle
                    label="Show Chat History Button"
                    checked={uiSettings.show_chat_history_button ?? true}
                    onChange={(_, checked) => updateUISettings('show_chat_history_button', checked ?? true)}
                    onText="Shown"
                    offText="Hidden"
                  />
                  <Text variant="small" style={{ color: '#605e5c', marginLeft: '20px' }}>
                    Controls the visibility of the chat history button and functionality.
                  </Text>
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                  <Toggle
                    label="Enable Chat History"
                    checked={uiSettings.enable_chat_history ?? true}
                    onChange={(_, checked) => updateUISettings('enable_chat_history', checked ?? true)}
                    onText="Enabled"
                    offText="Disabled"
                  />
                  <Text variant="small" style={{ color: '#605e5c', marginLeft: '20px' }}>
                    Controls whether chat history saving and loading functionality is enabled.
                  </Text>
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                  <Toggle
                    label="Show All Chat Control Buttons"
                    checked={uiSettings.show_upload_button ?? true}
                    onChange={(_, checked) => updateUISettings('show_upload_button', checked ?? true)}
                    onText="Shown"
                    offText="Hidden"
                  />
                  <Text variant="small" style={{ color: '#605e5c', marginLeft: '20px' }}>
                    Controls the visibility of all chat control buttons (upload, share, history, etc.). When disabled, only the "New Conversation" button will be shown.
                  </Text>
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                  <Toggle
                    label="Show Web Search Button"
                    checked={uiSettings.show_web_search_button ?? true}
                    onChange={(_, checked) => updateUISettings('show_web_search_button', checked ?? true)}
                    onText="Shown"
                    offText="Hidden"
                  />
                  <Text variant="small" style={{ color: '#605e5c', marginLeft: '20px' }}>
                    Controls the visibility of the web search toggle button in the chat interface.
                  </Text>
                </Stack>
              </Stack>
              
              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <PrimaryButton
                  iconProps={{ iconName: 'Save' }}
                  text="Save UI Settings"
                  onClick={saveUISettings}
                  disabled={uiLoading}
                />
                {uiLoading && <Spinner size={SpinnerSize.small} />}
              </Stack>
            </Stack>
          </PivotItem>
        </Pivot>
      </Stack>
    </div>
  );
};

export default UIManagementPage;
